import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  fetchProgramNewsUpdate,
  type ProgramNewsUpdate,
  type ProgramNewsUpdateDetailResponse,
} from "@/services/adminProgramNews";
import { currentAdminEmail } from "./adminEmail";
import EventBadges from "./EventBadges";
import { REASON_NOT_RECORDED } from "./glossary";
import { formatDate } from "./formatters";
import ReviewPanel from "./ReviewPanel";
import SourceArticleText from "./SourceArticleText";
import { formatSchoolName, splitTeamId } from "./teamLabel";
import { useReviewDetail } from "./useReviewDetail";

interface EventDetailSheetProps {
  update: ProgramNewsUpdate;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Lets the list refresh its reviewed badges. */
  onReviewChanged: () => void;
}

const EventDetailSheet = ({
  update,
  open,
  onOpenChange,
  onReviewChanged,
}: EventDetailSheetProps) => {
  const { data, refresh } = useReviewDetail<ProgramNewsUpdateDetailResponse>(open, () =>
    fetchProgramNewsUpdate(update._id)
  );
  const shown = data?.update ?? update;
  const { slug, gender } = splitTeamId(shown.teamId);

  const changed = async () => {
    await refresh();
    onReviewChanged();
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-3xl">
        <SheetHeader className="text-left">
          <SheetTitle>{shown.title}</SheetTitle>
          <SheetDescription>
            {formatSchoolName(slug)}
            {gender ? ` · ${gender}` : ""} · Event date {formatDate(shown.eventDate)}
          </SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-4">
          <div className="flex flex-wrap items-center gap-1 text-xs">
            <EventBadges update={shown} />
          </div>
          {shown.summary ? <p className="text-sm text-muted-foreground">{shown.summary}</p> : null}
          <div className="text-sm">
            <span className="font-medium">Why:</span>{" "}
            {shown.rationale ? (
              shown.rationale
            ) : (
              <span className="italic text-muted-foreground">{REASON_NOT_RECORDED}</span>
            )}
          </div>
          <div className="space-y-2">
            <h4 className="font-medium">Source articles</h4>
            {!data ? (
              <p className="text-sm text-muted-foreground">Loading source text...</p>
            ) : data.sourceItems.length === 0 ? (
              <p className="text-sm text-muted-foreground">No source articles are linked.</p>
            ) : (
              data.sourceItems.map(source => (
                <SourceArticleText
                  key={source._id}
                  title={source.title}
                  sourceUrl={source.sourceUrl}
                  publishedAt={source.publishedAt}
                  text={source.permittedText}
                />
              ))
            )}
          </div>
          {data ? (
            <ReviewPanel
              targetType="update"
              targetId={shown._id}
              reviews={data.reviews}
              myEmail={currentAdminEmail()}
              onChanged={changed}
            />
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default EventDetailSheet;
